import { useState } from 'react';

import { dateRangeShortcuts } from 'src/components/atoms/buttons/DateRangePicker';
import QuickDatePicker from 'src/components/atoms/QuickDatePicker';
import { AgButton } from 'src/components/ui';

import type { FilterItemProps } from './FilterItem';

const toDateInputValue = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const isPrismaComparableValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

export default function DateFilter({ updateFilter, filter, column, onClose }: FilterItemProps) {
  const colName = column.headerName ?? column.field;
  const [startDate, setStartDate] = useState<string | null>(() => {
    const val = filter.where?.find((w) => w.gte)?.gte;
    return toDateInputValue(column.serverFilterValueTransform?.from?.(val) ?? val);
  });
  const [endDate, setEndDate] = useState<string | null>(() => {
    const val = filter.where?.find((w) => w.lte)?.lte;
    return toDateInputValue(column.serverFilterValueTransform?.from?.(val) ?? val);
  });

  return (
    <>
      <div className="flex flex-row p-2 gap-2 items-center">
        {dateRangeShortcuts.map((shortcut) => (
          <AgButton
            key={shortcut.label}
            variant="white"
            size="xs"
            shape="pill"
            className="text-14 px-2"
            onPress={() => {
              const [start, end] = shortcut.getRange();
              setStartDate(start.setZone('utc', { keepLocalTime: true }).toISO());
              setEndDate(end.setZone('utc', { keepLocalTime: true }).toISO());
            }}
          >
            {shortcut.label}
          </AgButton>
        ))}
      </div>
      <div className="flex flex-row px-2 pb-2 gap-2 items-center">
        <QuickDatePicker
          className="grow"
          value={startDate}
          onChange={(date) => setStartDate(date)}
          slotProps={{ textField: { size: 'small', slotProps: { htmlInput: { 'aria-label': `${colName} from` } } } }}
        />
        <QuickDatePicker
          className="grow"
          value={endDate}
          onChange={(date) => setEndDate(date)}
          slotProps={{ textField: { size: 'small', slotProps: { htmlInput: { 'aria-label': `${colName} to` } } } }}
        />
      </div>
      <div className="flex justify-between gap-2 p-2 border-t border-neutral-5">
        <AgButton variant="destructive-white" onPress={() => updateFilter({ field: filter.field, where: [] })}>
          Clear
        </AgButton>
        <AgButton
          variant="primary"
          onPress={() => {
            const where: FilterItemProps['filter']['where'] = [];
            const fromValue = column.serverFilterValueTransform?.to?.(startDate) ?? startDate;
            const toValue = column.serverFilterValueTransform?.to?.(endDate) ?? endDate;
            if (fromValue && isPrismaComparableValue(fromValue)) where.push({ gte: fromValue });
            if (toValue && isPrismaComparableValue(toValue)) where.push({ lte: toValue });
            updateFilter({ field: filter.field, where });
            onClose?.();
          }}
        >
          Apply
        </AgButton>
      </div>
    </>
  );
}
